import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';
import { createCoalescedRunner } from '@/renderer/utils/coalesce';
import { useAuthToken } from '@/renderer/utils/useMe';
import useWebSocket, { WS_BASE_URL } from '@/renderer/utils/useWebSocket2';

// Non-future pending — matches the My Results pending section (MemberPendingSection)
// filter exactly, so this count and that section always agree.
const NON_FUTURE_PLAY_TYPES = ['side', 'total', 'prop', 'prop-group'] as const;

// A release NOTIFY delivers a burst of messageReceipt/created + /updated events,
// and this hook is mounted twice (nav badge + release-center notice), each with
// its own WS subscription. Before #1801 every event triggered an immediate,
// un-throttled refetch of the expensive limit-100 getPendingWithGhosts query, so
// one release became ~2N simultaneous pending queries across N online clients and
// 503'd the DB pool. Coalesce the whole burst — across both mounts — into ONE
// trailing refetch. Both mounts read the same shared infinite-store bank (same
// `navPendingCount` key), so a single refetch updates the count everywhere.
const PENDING_REFETCH_DEBOUNCE_MS = 1000;
const pendingRefetchRunner = createCoalescedRunner(PENDING_REFETCH_DEBOUNCE_MS);

/**
 * The signed-in member's count of ungraded, non-future pending picks across all
 * sports — the SAME entitlement-based source the My Results pending section uses.
 *
 * `type: 'release' + result: 'pending'` routes this to the server's
 * `getPendingWithGhosts`, so the count includes "ghost" picks the member is
 * entitled to via an active membership even without a messageReceipt (a fresh or
 * returning subscriber). That's why a sub with no receipts still gets a correct
 * "N pending" instead of 0.
 *
 * Shared by the nav badge (PendingCountBadge) and the Release Center pending
 * affordance. Both mount this with the same infinite-store key, so they share one
 * request and can never disagree.
 *
 * Live-updates (from #1455/#1459): this request skips polling and has no infinite-
 * store WS subscription of its own, so nothing would refetch it while it stays
 * mounted. Subscribe to the same live signals the rest of the app uses and re-derive
 * the count from the server on each: a new release (`created`) ticks it up, a grade
 * (`updated`) drops the play from the `pending` filter and ticks it down.
 */
export function usePendingPicksCount(): { count: number; loggedIn: boolean } {
  const authToken = useAuthToken();
  const loggedIn = !!authToken;

  const request = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
  >(
    useMemo(
      () => ({
        key: 'navPendingCount',
        req: '/api/v1/messageReceipt/me',
        query: {
          page: { limit: 100, offset: 0 },
          data: {
            type: 'release',
            result: 'pending',
            playType: [...NON_FUTURE_PLAY_TYPES],
          },
        },
        skipPoll: true,
        pause: !loggedIn,
      }),
      [loggedIn],
    ),
  );

  const refetchRef = useRef(request.refetch);
  refetchRef.current = request.refetch;

  // Register this mount's refetch with the shared coalescer while mounted. The
  // coalescer only ever runs one registrant per burst; since both mounts share
  // the same store bank, that single refetch updates the count for all of them.
  useEffect(() => {
    if (!loggedIn) {
      return;
    }
    return pendingRefetchRunner.register(() => refetchRef.current?.());
  }, [loggedIn]);

  const onReceiptChanged = useCallback(() => {
    pendingRefetchRunner.schedule();
  }, []);

  useWebSocket(
    WS_BASE_URL,
    useMemo(
      () => ({
        connect: loggedIn,
        subscriptions: {
          'messageReceipt/created': onReceiptChanged,
          'messageReceipt/updated': onReceiptChanged,
        },
      }),
      [loggedIn, onReceiptChanged],
    ),
  );

  // getPendingWithGhosts sets pageInfo.total to the ghost-inclusive count when it
  // augments with ghosts; otherwise it skips the COUNT (#1801) and total is
  // absent, so fall back to the returned rows (the exact count while under the
  // limit-100 page — which a member's pending slate always is).
  const count = request.data?.pageInfo?.total ?? request.data?.data?.length ?? 0;

  return { count: loggedIn ? count : 0, loggedIn };
}
